import { Button, Dropdown, Space, Table, Tag, Typography } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";

const statusColors = {
  succeeded: { bg: "#e6fffb", color: "#006d75", border: "#87e8de" },
  pending: { bg: "#fffbe6", color: "#ad8b00", border: "#ffe58f" },
  failed: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
};

const normalizeStatus = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["succeeded", "completed", "paid", "success"].includes(normalized)) {
    return "succeeded";
  }
  if (
    ["pending", "requires_capture", "processing", "requires_action"].includes(
      normalized,
    )
  ) {
    return "pending";
  }
  if (["failed", "canceled", "cancelled"].includes(normalized)) {
    return "failed";
  }
  return "other";
};

const formatStatusLabel = (status) =>
  String(status || "-").replaceAll("_", " ").toUpperCase();

const getTagStyle = (status) => {
  const colors = statusColors[normalizeStatus(status)];
  return {
    backgroundColor: colors?.bg || "#f5f5f5",
    color: colors?.color || "#262626",
    padding: "2px 10px",
    borderRadius: "6px",
    fontSize: "0.65rem",
    border: `1px solid ${colors?.border || "#d9d9d9"}`,
    fontWeight: 600,
    cursor: "default",
  };
};

const resolveChargeCode = (record) =>
  record?.chargeCode ||
  record?.chargeItemCode ||
  record?.paymentChargeDetails?.chargeItemCode ||
  record?.chargeDetails?.chargeItemCode ||
  record?.charge?.code ||
  record?.code ||
  "-";

const resolveTransactionReference = (record) =>
  record?.paymentProcessorId ||
  record?.paymentChargeDetails?.paymentProcessorId ||
  record?.chargeDetails?.paymentProcessorId ||
  record?.transactionReference ||
  record?.paymentChargeDetails?.paymentIntentId ||
  record?.chargeDetails?.paymentIntentId ||
  record?.paymentIntentId ||
  record?.stripePaymentIntent?.id ||
  record?.id ||
  "-";

const resolveTransactionDate = (record) =>
  record?.succeededAt || record?.failedAt || record?.createdAt || null;

const renderPartyCell = (party) => {
  if (!party) return "-";
  const fullName = [party.firstName, party.lastName].filter(Boolean).join(" ");
  const fallbackName = fullName || party.email || "-";

  return (
    <div style={{ lineHeight: 1.25 }}>
      <div style={{ fontWeight: 500 }}>{fallbackName}</div>
      {party.email ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {party.email}
        </Typography.Text>
      ) : null}
    </div>
  );
};

const formatCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  const currencyCode = (currency || "USD").toUpperCase();

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch (err) {
    return `${numericAmount.toFixed(2)} ${currencyCode}`;
  }
};

const formatDateTime = (value) =>
  value ? dayjs(value).format("YYYY-MM-DD hh:mm A") : "-";

const renderPartyDetails = (party) => {
  if (!party) return "-";
  const fullName = [party.firstName, party.lastName].filter(Boolean).join(" ");
  return fullName || party.email || "-";
};

const renderJsonPreview = (value) => {
  if (!value) return "-";
  return (
    <pre style={{ maxHeight: 180, overflow: "auto", margin: 0 }}>
      {JSON.stringify(value, null, 2)}
    </pre>
  );
};

const resolvePaymentMethodLabel = (record) => {
  const paymentMethod = record?.paymentMethod;
  if (!paymentMethod) return "-";

  if (paymentMethod?.card?.brand || paymentMethod?.card?.last4) {
    return `${paymentMethod.card.brand || "Card"} **** ${
      paymentMethod.card.last4 || ""
    }`.trim();
  }

  if (paymentMethod?.bankName) return paymentMethod.bankName;
  if (paymentMethod?.stripePaymentMethodId) {
    return paymentMethod.stripePaymentMethodId;
  }

  return "-";
};

export default function PaymentTransactionsTable({
  transactions = [],
  loading,
  meta,
  onFetchTransactions,
  isClientFiltered = false,
  onOpenRefunds,
}) {
  const navigate = useNavigate();
  const columns = [
    {
      title: "Charge Code",
      key: "chargeCode",
      width: 150,
      render: (_, record) => resolveChargeCode(record),
    },
    {
      title: "Transaction Date",
      key: "transactionDate",
      width: 170,
      sorter: (a, b) =>
        dayjs(resolveTransactionDate(a) || 0).valueOf() -
        dayjs(resolveTransactionDate(b) || 0).valueOf(),
      render: (_, record) => formatDateTime(resolveTransactionDate(record)),
    },
    {
      title: "Payer",
      key: "payer",
      width: 220,
      ellipsis: true,
      render: (_, record) => renderPartyCell(record.payer),
    },
    {
      title: "Payee",
      key: "payee",
      width: 220,
      ellipsis: true,
      render: (_, record) => renderPartyCell(record.payee),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      align: "right",
      sorter: (a, b) => Number(a?.amount || 0) - Number(b?.amount || 0),
      render: (amount, record) => (
        <Typography.Text style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatCurrency(amount, record.currency)}
        </Typography.Text>
      ),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 120,
      render: (currency) => currency?.toUpperCase() || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => (
        <Tag style={getTagStyle(status)}>{formatStatusLabel(status)}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      sorter: (a, b) =>
        dayjs(a?.createdAt || 0).valueOf() - dayjs(b?.createdAt || 0).valueOf(),
      render: (value) => formatDateTime(value),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      render: (_, record) => {
        const menuItems = [
          {
            key: "issue_refund",
            label: "Issue refund",
            onClick: () => onOpenRefunds?.(record),
          },
          {
            key: "view_history",
            label: "View refund history",
            onClick: () => {
              if (!record?.id) return;
              navigate(paths.paymentRefundHistory(record.id));
            },
          },
        ];

        return (
          <Dropdown
            trigger={["click"]}
            menu={{ items: menuItems }}
            placement="bottomRight"
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleTableChange = (pagination) => {
    if (isClientFiltered) return;
    if (!onFetchTransactions) return;
    onFetchTransactions({
      page: pagination.current - 1,
      size: pagination.pageSize,
    });
  };

  return (
    <Table
      rowKey={(record) => record.id || resolveTransactionReference(record)}
      columns={columns}
      dataSource={transactions}
      loading={loading}
      className="custom-table"
      bordered
      size="small"
      scroll={{ x: 1320 }}
      tableLayout="fixed"
      style={{ borderRadius: 10, overflow: "hidden" }}
      title={() => (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography.Text strong>Transactions</Typography.Text>
        
        </div>
      )}
      pagination={
        isClientFiltered
          ? {
              pageSize: 20,
              size: "small",
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            }
          : {
              current: (meta?.number ?? meta?.page ?? 0) + 1,
              pageSize: meta?.size || 20,
              total: meta?.totalElements || transactions.length,
              size: "small",
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            }
      }
      onChange={handleTableChange}
      expandable={{
        expandedRowRender: (record) => (
          <div style={{ paddingLeft: 50 }}>
            <p className="mb-1 pb-0">
              <strong>Transaction Ref:</strong> {resolveTransactionReference(record)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Charge Code:</strong> {resolveChargeCode(record)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payer:</strong> {renderPartyDetails(record.payer)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payee:</strong> {renderPartyDetails(record.payee)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Amount:</strong> {formatCurrency(record.amount, record.currency)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Status:</strong> {formatStatusLabel(record.status)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payment Status:</strong> {record.paymentStatus || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payment Method:</strong> {resolvePaymentMethodLabel(record)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Description:</strong> {record.description || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Notes:</strong> {record.notes || "-"}
            </p>
            <p className="mb-1 pb-0">
              <strong>Created At:</strong> {formatDateTime(record.createdAt)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Updated At:</strong> {formatDateTime(record.updatedAt)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Stripe Payment Intent:</strong>
              {renderJsonPreview(record.stripePaymentIntent)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Payment Charge Details:</strong>
              {renderJsonPreview(record.paymentChargeDetails)}
            </p>
            <p className="mb-1 pb-0">
              <strong>Charge Details:</strong>
              {renderJsonPreview(record.chargeDetails)}
            </p>
          </div>
        ),
        rowExpandable: () => true,
      }}
      locale={{
        emptyText: "No payment transactions found for the selected filters.",
      }}
    />
  );
}
